import React, { useEffect, useState } from 'react';
import { Upload } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { InboxOutlined } from '@ant-design/icons';
import { Button, CircularProgress } from '@mui/material';

import {
    deleteFileUploadErrorEntries,
    onClearDocumentErrors
} from '../../../store/documentService';
import { POLARIS_ROYAL_BLUE } from '../../../themes/theme';
import { regExps } from '../../../utils/formValidation/validationPatterns';
import IconError from '../../IconError';

import '../../../commonStyles/antUploaderStyles.css';
import modal from '../../../commonStyles/modal.module.css';
import uploader from '../../../commonStyles/uploader.module.css';
import local from '../uploaders.module.css';

const { Dragger } = Upload;

const _FILE_TYPES = '.csv, .pdf';
const _MAX_FILE_SIZE = 25;
const acceptedFileTypes = ['text/csv', 'application/pdf'];

const _ERROR_DISPLAY_TIME = 7000;

const Uploader = ({ uploadData, onUpload, onClose, onCancel, isDisabled = false }) => {
    const dispatch = useDispatch();

    const [csvFileName, setCsvFileName] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [errors, setErrors] = useState({});

    const uploadErrors = useSelector((state) => state.entities.documentService.uploadErrors);
    const uploadErrorFiles = useSelector(
        (state) => state.entities.documentService.uploadErrorFiles
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    const uploadProps = {
        name: 'file',
        onDrop(e) {
            const allowedFiles = Object.values(e.dataTransfer.files).filter(
                (el) =>
                    acceptedFileTypes.includes(el.type) &&
                    regExps.PT_BATCH_FILE_NAME_R.test(el.name)
            );
            const wrongFileTypes = Object.values(e.dataTransfer.files).filter(
                (el) => !acceptedFileTypes.includes(el.type)
            );

            const wrongFileNames = Object.values(e.dataTransfer.files).filter(
                (el) => !regExps.PT_BATCH_FILE_NAME_R.test(el.name)
            );

            const totalSize = allowedFiles.map((el) => el.size).reduce((acc, cur) => acc + cur, 0);
            const isValidSize = totalSize / 1024 / 1024 <= _MAX_FILE_SIZE;

            if (!isValidSize) {
                const message = `Total file upload size must be smaller than ${_MAX_FILE_SIZE}MB`;
                configFileError('fileSize', message);
                return false;
            }
            if (wrongFileTypes.length > 0) {
                const message = `${wrongFileTypes.length} file${wrongFileTypes.length > 1 ? 's' : ''} could not be uploaded as the file type is not supported.`;
                configFileError('fileType', message);
            }
            if (wrongFileNames.length > 0) {
                const message = `${wrongFileNames.length} file${wrongFileNames.length > 1 ? 's' : ''} could not be uploaded as ${wrongFileNames.length > 1 ? 'they are' : 'it is'} not named with the prefix 'PO_' followed by digits`;
                configFileError('fileName', message);
            }
            allowedFiles.length > 0 && setFileList(allowedFiles);
        },
        multiple: true,
        customRequest(info) {
            info.onPending && console.info('Files uploading.');
        }
    };

    useEffect(() => {
        if (fileList.length < 1) return;
        const fileStatuses = fileList.map((el) => ({ name: el.name, status: el.status }));
        setCsvFileName(fileStatuses[0]?.name);
        const payload = new FormData();
        fileList.forEach((el) => payload.append('file', el));
        setFileList([]);
        if (uploadData) uploadData.forEach((el) => payload.append(el.key, el.value));
        setIsPending(true);
        const successMsg = `Documents ${fileList.map((el) => el.name)} have been uploaded`;
        setSuccessMsg(successMsg);
        onUpload(payload, successMsg, errors);
    }, [fileList]);

    useEffect(() => {
        setIsPending(false);
        if (
            uploadErrors?.length < 1 &&
            Object.keys(uploadErrorFiles).length < 1 &&
            successMessage !== '' &&
            successMessage === successMsg
        ) {
            onFormExit();
            onClose();
        }
        if (uploadErrors?.length) {
            onClose(uploadErrors, csvFileName);
            dispatch(onClearDocumentErrors());
        }
        if (Object.keys(uploadErrorFiles)?.length) {
            onClose();
        }
    }, [successMessage, uploadErrors, uploadErrorFiles]);

    // FNs
    const configFileError = (key, message) => {
        setErrors((errors) => ({ ...errors, [key]: { error: true, message } }));
        setTimeout(() => setErrors({}), _ERROR_DISPLAY_TIME);
    };

    // EVENT HANDLERS
    const onFormExit = async () => {
        uploadErrors?.length && dispatch(deleteFileUploadErrorEntries());
    };

    const onExit = () => {
        onFormExit();
        onCancel();
    };

    return (
        <div className={modal.modalUploaderContent}>
            <div className={local.modalUploaderForm}>
                <div className={local.uploader}>
                    {isPending && (
                        <div className={uploader.loadingSpinner}>
                            <CircularProgress sx={{ color: POLARIS_ROYAL_BLUE }} />
                        </div>
                    )}
                    <Dragger
                        accept={_FILE_TYPES}
                        onDrop={(e) => uploadProps.onDrop(e)}
                        data-testid={`dragger`}
                        {...uploadProps}
                        disabled={isDisabled}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Drag file(s) to this area to upload</p>
                        <p className="ant-upload-hint">{`Support for fileTypes: ${_FILE_TYPES}`}</p>
                    </Dragger>
                </div>

                {Object.values(errors).some((el) => el.error) &&
                    Object.entries(errors).map((el) =>
                        el[1].error ? (
                            <div key={el[0]}>
                                <IconError text={el[1]} />
                            </div>
                        ) : null
                    )}

                <div className={modal.actionButtons}>
                    <div className={modal.cancelButton}>
                        <Button variant={'contained'} onClick={onExit}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

Uploader.propTypes = {
    uploadData: PropTypes.arrayOf(PropTypes.object),
    onUpload: PropTypes.func,
    onClose: PropTypes.func,
    onCancel: PropTypes.func,
    isDisabled: PropTypes.bool
};

export default Uploader;
